export type Health = 'healthy' | 'degraded' | 'down';
export type NodeKind = 'frontend' | 'service' | 'datastore' | 'external' | 'link';

export interface ServiceNode {
  id: string;
  label: string;
  kind: NodeKind;
  health: Health;
  p50Ms: number | null;
  p99Ms: number | null;
  errorRatePct: number | null;
  uptimePct: number | null;
  note: string;
  /** kind === 'link'일 때만 — 클릭하면 이 사업 탭으로 건너뛴다. */
  linkTo?: string;
}

export interface ServiceEdge {
  from: string;
  to: string;
  label?: string;
  external?: boolean;
}

export interface Business {
  id: string;
  label: string;
  summary: string;
  /** 이 사업의 중심 노드 — 서비스맵이 이 노드를 캔버스 정중앙에 놓고 나머지를 hop 수만큼 방사형으로 배치한다. */
  hub: string;
}

/**
 * 탭 = repo가 아니라 **사업 단위**다. 워크스페이스 루트 CLAUDE.md는 남도마켓 계열 repo만 나열하고
 * uncle-* 6개 repo는 빠져 있었다 — `SERVICE_MAP.md`(2026-06-02, 남도마켓/복지몰 실측)와 각 uncle repo
 * 문서(`uncle-pos/docs/legal/pos-service-overview-for-legal.md`, `uncle-backend/README.md` 등)를 근거로
 * 다시 짰다. "엉클"은 사입삼촌(uncle-frontend/backend/ai)과 UNCLE POS(uncle-pos)를 한 탭에 묶은 것 —
 * 브랜드명은 같지만 조직·코드는 분리돼 있다(각 노드 note에 구분해 뒀다).
 *
 * 좌표는 더 이상 데이터에 없다 — `ServiceMapView`가 `hub` 기준 BFS로 매번 계산한다(허브가 중앙,
 * 나머지는 hop 수만큼 좌/우로 방사). 남도마켓은 QR랜딩·펭디 프론트/챗봇 분리 등 지엽적인 노드를
 * 줄이고 합쳐서(펭디 프론트+챗봇 → 1노드, Bedrock+LLM API → 1노드) 한눈에 들어오게 했다.
 */
export const BUSINESSES: Business[] = [
  { id: 'namdo-market', label: '남도마켓', summary: 'B2B 도소매 커머스 플랫폼 — 코어(펭디 포함)', hub: 'nd-market-api' },
  { id: 'welfare-mall', label: '복지몰', summary: 'careonplus · 멀티테넌트 복지포인트몰 — DB·도메인 독립', hub: 'nd-welfare-api' },
  { id: 'uncle', label: '엉클', summary: '사입삼촌(도매 소싱) + UNCLE POS — 별도 사업, 남도마켓과 교차 연동만', hub: 'uncle-backend' },
];

export const NODES: Record<string, ServiceNode[]> = {
  'namdo-market': [
    { id: 'vue_retail', label: 'vue_retail', kind: 'frontend', health: 'healthy', p50Ms: null, p99Ms: null, errorRatePct: null, uptimePct: 99.98, note: '소매/도매 웹' },
    { id: 'vue_admin', label: 'vue_admin', kind: 'frontend', health: 'healthy', p50Ms: null, p99Ms: null, errorRatePct: null, uptimePct: 99.9, note: '남도마켓 관리자(17개 도메인)' },
    { id: 'ndmall-nextjs', label: 'ndmall-nextjs', kind: 'frontend', health: 'healthy', p50Ms: null, p99Ms: null, errorRatePct: null, uptimePct: 99.95, note: '남도몰(수출용 i18n 스토어)' },

    { id: 'nd-market-api', label: 'nd-market-api', kind: 'service', health: 'healthy', p50Ms: 82, p99Ms: 340, errorRatePct: 0.12, uptimePct: 99.97, note: '메인 B2B 도매/소매 API — 허브' },
    { id: 'nd-es-bridge', label: 'nd-es-bridge', kind: 'service', health: 'degraded', p50Ms: 210, p99Ms: 1450, errorRatePct: 2.4, uptimePct: 99.2, note: '검색 색인·조회 브릿지' },
    { id: 'parcel-tracker', label: 'parcel-tracker', kind: 'service', health: 'healthy', p50Ms: 65, p99Ms: 280, errorRatePct: 0.31, uptimePct: 99.9, note: '배송 추적' },
    { id: 'nd-transflow', label: 'nd-transflow', kind: 'service', health: 'healthy', p50Ms: 340, p99Ms: 1100, errorRatePct: 0.2, uptimePct: 99.8, note: '상품 번역(Gemini/Translate)' },
    { id: 'pengdi', label: '펭디', kind: 'service', health: 'down', p50Ms: null, p99Ms: null, errorRatePct: 100, uptimePct: 91.8, note: 'AI 쇼핑도우미(프론트+챗봇 통합 표기)' },

    { id: 'mysql', label: 'MySQL(공유)', kind: 'datastore', health: 'healthy', p50Ms: 4, p99Ms: 22, errorRatePct: 0, uptimePct: 99.99, note: 'market·es-bridge·transflow·펭디 공유' },
    { id: 'redis-a', label: 'Redis A', kind: 'datastore', health: 'healthy', p50Ms: 2, p99Ms: 14, errorRatePct: 0, uptimePct: 100, note: 'market ↔ 펭디 공유(키 충돌 주의)' },
    { id: 'rabbitmq', label: 'RabbitMQ', kind: 'datastore', health: 'healthy', p50Ms: 3, p99Ms: 18, errorRatePct: 0, uptimePct: 99.95, note: '단일 공유 인스턴스(복지몰도 사용)' },
    { id: 'elasticsearch', label: 'Elasticsearch', kind: 'datastore', health: 'degraded', p50Ms: 180, p99Ms: 1200, errorRatePct: 1.8, uptimePct: 99.5, note: '상품·매장 색인' },

    { id: 'llm-ai-apis', label: 'LLM/AI APIs', kind: 'external', health: 'healthy', p50Ms: 380, p99Ms: 1500, errorRatePct: 0.5, uptimePct: 99.7, note: 'OpenAI·Anthropic·Gemini·Bedrock(임베딩)' },
    { id: 'sweettracker', label: 'SweetTracker', kind: 'external', health: 'healthy', p50Ms: 320, p99Ms: 980, errorRatePct: 0.6, uptimePct: 99.7, note: '외부 배송조회 연동' },
    { id: 'link-uncle', label: '엉클(사입삼촌)', kind: 'link', health: 'healthy', p50Ms: null, p99Ms: null, errorRatePct: null, uptimePct: null, note: '다른 사업 · 통계 API만 조회', linkTo: 'uncle' },
  ],

  'welfare-mall': [
    { id: 'careonplus-client', label: 'careonplus 클라이언트', kind: 'frontend', health: 'healthy', p50Ms: null, p99Ms: null, errorRatePct: null, uptimePct: 99.9, note: 'careonplus.com' },

    { id: 'nd-welfare-api', label: 'nd-welfare-api', kind: 'service', health: 'healthy', p50Ms: 96, p99Ms: 410, errorRatePct: 0.08, uptimePct: 99.99, note: '복지몰 API(멀티테넌트) — 허브' },

    { id: 'postgres', label: 'PostgreSQL', kind: 'datastore', health: 'healthy', p50Ms: 5, p99Ms: 28, errorRatePct: 0, uptimePct: 99.99, note: 'welfare 전용(멀티테넌트)' },
    { id: 'redis-b', label: 'Redis B', kind: 'datastore', health: 'healthy', p50Ms: 3, p99Ms: 16, errorRatePct: 0, uptimePct: 100, note: 'welfare 전용' },
    { id: 'rabbitmq-w', label: 'RabbitMQ', kind: 'datastore', health: 'healthy', p50Ms: 3, p99Ms: 18, errorRatePct: 0, uptimePct: 99.95, note: '남도마켓과 공유 인스턴스' },
    { id: 'gcs-w', label: 'GCS', kind: 'datastore', health: 'healthy', p50Ms: 30, p99Ms: 120, errorRatePct: 0.02, uptimePct: 99.99, note: '첨부·리포트 저장' },

    { id: 'kicc', label: 'KICC EasyPay', kind: 'external', health: 'healthy', p50Ms: 280, p99Ms: 900, errorRatePct: 0.3, uptimePct: 99.8, note: '결제 PG' },
    { id: 'popbill-w', label: 'Popbill', kind: 'external', health: 'healthy', p50Ms: 150, p99Ms: 500, errorRatePct: 0.1, uptimePct: 99.9, note: '알림톡' },
    { id: 'slack-w', label: 'Slack Webhook', kind: 'external', health: 'healthy', p50Ms: 90, p99Ms: 300, errorRatePct: 0.05, uptimePct: 99.95, note: 'order/queue/모니터링 알림' },
  ],

  uncle: [
    { id: 'uncle-frontend', label: 'uncle-frontend', kind: 'frontend', health: 'healthy', p50Ms: null, p99Ms: null, errorRatePct: null, uptimePct: 99.9, note: '사입삼촌 고객 웹(Vue3)' },

    { id: 'uncle-backend', label: 'uncle-backend', kind: 'service', health: 'healthy', p50Ms: 110, p99Ms: 480, errorRatePct: 0.2, uptimePct: 99.9, note: '사입삼촌 주문·정산(NestJS) — 허브' },
    { id: 'uncle-ai', label: 'uncle-ai', kind: 'service', health: 'degraded', p50Ms: 620, p99Ms: 2100, errorRatePct: 1.6, uptimePct: 99.1, note: '이미지분석·HS코드/가격예측·번역' },
    { id: 'uncle-pos', label: 'uncle-pos', kind: 'service', health: 'healthy', p50Ms: 140, p99Ms: 520, errorRatePct: 0.15, uptimePct: 99.85, note: 'UNCLE POS · 별도 SaaS(의류 도매 매장용, 법인관계 미확정 — uncle-backend와 코드상 무관)' },

    { id: 'supabase-pg', label: 'Supabase(Postgres)', kind: 'datastore', health: 'healthy', p50Ms: 6, p99Ms: 30, errorRatePct: 0, uptimePct: 99.95, note: 'uncle-pos 전용 · ap-northeast-2' },

    { id: 'link-namdo-marketapi', label: '남도마켓 nd-market-api', kind: 'link', health: 'healthy', p50Ms: null, p99Ms: null, errorRatePct: null, uptimePct: null, note: '다른 사업 · 온라인 주문 동기화', linkTo: 'namdo-market' },
    { id: 'link-namdo-vueadmin', label: '남도마켓 vue_admin', kind: 'link', health: 'healthy', p50Ms: null, p99Ms: null, errorRatePct: null, uptimePct: null, note: '다른 사업 · 통계 조회만', linkTo: 'namdo-market' },
  ],
};

export const EDGES: Record<string, ServiceEdge[]> = {
  'namdo-market': [
    { from: 'vue_retail', to: 'nd-market-api' },
    { from: 'vue_admin', to: 'nd-market-api' },
    { from: 'vue_admin', to: 'link-uncle', label: '통계 조회(Bearer)', external: true },
    { from: 'ndmall-nextjs', to: 'nd-market-api' },
    { from: 'nd-market-api', to: 'nd-es-bridge', label: '검색 HTTP' },
    { from: 'nd-market-api', to: 'nd-transflow', label: '번역(Cloud Tasks)' },
    { from: 'nd-market-api', to: 'rabbitmq', label: '배송요청 publish' },
    { from: 'rabbitmq', to: 'parcel-tracker', label: 'consume' },
    { from: 'parcel-tracker', to: 'sweettracker', external: true },
    { from: 'nd-market-api', to: 'mysql' },
    { from: 'nd-es-bridge', to: 'mysql' },
    { from: 'nd-transflow', to: 'mysql' },
    { from: 'pengdi', to: 'mysql' },
    { from: 'nd-market-api', to: 'redis-a' },
    { from: 'pengdi', to: 'redis-a' },
    { from: 'nd-es-bridge', to: 'elasticsearch' },
    { from: 'nd-market-api', to: 'llm-ai-apis' },
    { from: 'nd-es-bridge', to: 'llm-ai-apis', label: '임베딩' },
    { from: 'nd-transflow', to: 'llm-ai-apis' },
    { from: 'pengdi', to: 'llm-ai-apis' },
  ],

  'welfare-mall': [
    { from: 'careonplus-client', to: 'nd-welfare-api' },
    { from: 'nd-welfare-api', to: 'postgres' },
    { from: 'nd-welfare-api', to: 'redis-b' },
    { from: 'nd-welfare-api', to: 'rabbitmq-w', label: '공유 인스턴스' },
    { from: 'nd-welfare-api', to: 'gcs-w' },
    { from: 'nd-welfare-api', to: 'kicc' },
    { from: 'nd-welfare-api', to: 'popbill-w' },
    { from: 'nd-welfare-api', to: 'slack-w' },
  ],

  uncle: [
    { from: 'uncle-frontend', to: 'uncle-backend' },
    { from: 'uncle-backend', to: 'uncle-ai', label: '이미지분석·HS코드·번역' },
    { from: 'link-namdo-vueadmin', to: 'uncle-backend', label: '통계 조회(Bearer)', external: true },
    { from: 'uncle-pos', to: 'supabase-pg' },
    { from: 'uncle-pos', to: 'link-namdo-marketapi', label: '주문 동기화(OAuth2)', external: true },
  ],
};
